// frontend/src/components/LocalCompraModal/LocalCompraModal.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Form, Input, Select, AutoComplete, Alert, Typography, App } from 'antd';
import { createLocalCompra, updateLocalCompra } from '../../services/comprasService';
import { getDescricaoSuggestions, deleteDescricaoSuggestion } from '../../services/suggestionService';
import { mapDescricaoSuggestions } from '../../utils/descricaoSuggestions';
import { getErrorMessage } from '../../utils/errorHandler';

const { Text } = Typography;

export const TIPOS_LOCAL = [
  { value: 'SUPERMERCADO', label: 'Supermercado' },
  { value: 'ATACADO', label: 'Atacado / Atacarejo' },
  { value: 'FERRAGISTA', label: 'Ferragista / Material de construção' },
  { value: 'PET_SHOP', label: 'Pet shop' },
  { value: 'FARMACIA', label: 'Farmácia' },
  { value: 'PADARIA', label: 'Padaria' },
  { value: 'ACOUGUE', label: 'Açougue' },
  { value: 'HORTIFRUTI', label: 'Hortifrúti / Feira' },
  { value: 'CONVENIENCIA', label: 'Conveniência' },
  { value: 'MARKETPLACE', label: 'Marketplace' },
  { value: 'OUTRO', label: 'Outro' },
];

const NOMES_GENERICOS = new Set([
  'supermercado', 'super mercado', 'mercado', 'mercadinho', 'mercearia', 'atacado',
  'atacadao', 'atacarejo', 'hipermercado', 'ferragista', 'ferragem', 'material de construcao',
  'pet shop', 'petshop', 'pet', 'farmacia', 'drogaria', 'padaria', 'acougue', 'hortifruti',
  'sacolao', 'quitanda', 'feira', 'conveniencia', 'loja', 'distribuidora', 'deposito', 'armazem',
]);
const normalizarNome = (s) =>
  (s || '').normalize('NFD').replace(/[^\x00-\x7f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
export const nomeEhGenerico = (s) => NOMES_GENERICOS.has(normalizarNome(s));

function NomeGenericoAviso({ form }) {
  const nome = Form.useWatch('nome', form);
  if (!nome || !nomeEhGenerico(nome)) return null;
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={`"${nome.trim()}" parece um tipo de loja`}
      description={`Use o nome específico (ex.: "${nome.trim()} Tijuca") — é assim que o comparador de preços funciona.`}
    />
  );
}

/**
 * Modal de criar/editar local de compra — compartilhado entre a listagem e a
 * página de detalhe do local, para não haver duas cópias da lógica de
 * sugestão de descrição padrão (mesma fonte usada nos lançamentos financeiros).
 */
export default function LocalCompraModal({ open, local, categorias, onClose, onSaved }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [descricaoOptions, setDescricaoOptions] = useState([]);
  const [descricaoBusca, setDescricaoBusca] = useState('');
  const [modal, modalCtx] = Modal.useModal();

  useEffect(() => {
    if (!open) return;
    if (local) {
      form.setFieldsValue({
        nome: local.nome,
        tipo: local.tipo || undefined,
        categoria: local.categoria || undefined,
        descricao_padrao: local.descricao_padrao || '',
      });
    } else {
      form.resetFields();
    }
    setDescricaoBusca('');
    setDescricaoOptions([]);
  }, [open, local, form]);

  const handleRemoverSugestaoDescricao = useCallback((sugestao) => {
    modal.confirm({
      title: 'Remover sugestão',
      content: `"${sugestao.descricao}" não vai mais aparecer nas sugestões de descrição. Lançamentos já salvos não são afetados.`,
      okText: 'Remover',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteDescricaoSuggestion(sugestao.id);
          setDescricaoOptions((prev) => prev.filter((o) => o.id !== sugestao.id));
          message.success('Sugestão removida.');
        } catch (err) {
          message.error(getErrorMessage(err, 'Não foi possível remover a sugestão.'));
        }
      },
    });
  }, [modal, message]);

  useEffect(() => {
    if (!open || descricaoBusca.trim().length < 2) {
      setDescricaoOptions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      const resultados = await getDescricaoSuggestions(descricaoBusca.trim());
      setDescricaoOptions(mapDescricaoSuggestions(resultados, { onDelete: handleRemoverSugestaoDescricao }));
    }, 300);
    return () => clearTimeout(t);
  }, [descricaoBusca, open, handleRemoverSugestaoDescricao]);

  const selecionarDescricaoPadrao = (value, option) => {
    setDescricaoOptions([]);
    const patch = { descricao_padrao: value };
    if (option.categoria_id != null && !form.getFieldValue('categoria')) {
      patch.categoria = option.categoria_id;
    }
    form.setFieldsValue(patch);
  };

  const salvar = async (values) => {
    setSaving(true);
    try {
      const payload = {
        nome: values.nome.trim(),
        tipo: values.tipo ?? '',
        categoria: values.categoria ?? null,
        descricao_padrao: values.descricao_padrao?.trim() || '',
      };
      const salvo = local
        ? await updateLocalCompra(local.id, payload)
        : await createLocalCompra(payload);
      message.success(local ? 'Local atualizado.' : 'Local criado.');
      onSaved(salvo);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao salvar local.'));
    } finally {
      setSaving(false);
    }
  };

  const despesaCategorias = (categorias || []).filter((c) => c.tipo === 'despesa');

  return (
    <Modal
      title={local ? 'Editar local' : 'Novo local de compra'}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={saving}
      okText="Salvar"
      destroyOnClose
    >
      {modalCtx}
      <Form form={form} layout="vertical" onFinish={salvar}>
        <Form.Item
          name="nome"
          label="Nome do estabelecimento"
          rules={[{ required: true, message: 'Informe o nome.' }]}
          tooltip="Use o nome específico da loja — é assim que o comparador de preços funciona."
        >
          <Input placeholder="Ex.: Assaí Aterrado, Extra Vila Isabel" maxLength={120} />
        </Form.Item>
        <NomeGenericoAviso form={form} />
        <Form.Item name="tipo" label="Tipo de loja">
          <Select allowClear placeholder="Opcional — só para agrupar" options={TIPOS_LOCAL} />
        </Form.Item>
        <Form.Item
          name="categoria"
          label="Categoria padrão do lançamento"
          tooltip="Ao concluir uma lista deste local, o lançamento vem com esta categoria."
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Escolha uma categoria de despesa"
            options={despesaCategorias.map((c) => ({ value: c.id, label: c.nome }))}
          />
        </Form.Item>
        <Form.Item
          name="descricao_padrao"
          label="Descrição padrão do lançamento"
          tooltip="Texto sugerido para a descrição quando você concluir uma lista deste local. Sugestões vêm dos seus lançamentos já feitos."
        >
          <AutoComplete
            options={descricaoOptions}
            onSearch={setDescricaoBusca}
            onSelect={selecionarDescricaoPadrao}
            filterOption={false}
          >
            <Input placeholder="Ex.: Compra do mês" maxLength={255} />
          </AutoComplete>
        </Form.Item>
        <Text type="secondary" style={{ fontSize: 12 }}>
          A conta ou cartão é escolhida na hora de lançar a compra — costuma variar.
        </Text>
      </Form>
    </Modal>
  );
}
